/* eslint-disable new-cap */
/* eslint-disable no-param-reassign */
import React from 'react';
import { useSelector } from 'react-redux';

import { useD3 } from '../../hooks/useD3';
import d3 from '../../utils/d3Importer';
import { getColour } from '../../utils/functions';

export default function Content() {
  const data = useSelector((state) => state.data.data);
  const type = useSelector((state) => state.data.type);
  const stageWidth = window.innerWidth > 960 ? 960 : window.innerWidth;

  const margin = { top: 20, right: 20, bottom: 50, left: 50 };

  const mode = type;

  function getLevelCutoff() {
    if (mode === 'POP' && window.innerWidth > 630) {
      return 130000000;
    }
    if (mode === 'POP' && window.innerWidth < 630) {
      return 1130000000;
    }
    if (mode !== 'POP' && window.innerWidth > 630) {
      return 1100000000;
    }
    if (mode !== 'POP' && window.innerWidth < 630) {
      return 2100000000;
    }
    return 11100000000;
  }
  const level = getLevelCutoff();

  const ellipse = d3
    .range(100)
    .map((i) => [
      (stageWidth * (1 + 0.99 * Math.cos((i / 50) * Math.PI))) / 2,
      (stageWidth * (1 + 0.99 * Math.sin((i / 50) * Math.PI))) / 2,
    ]);

  function colorHierarchy(hierarchy) {
    if (hierarchy.depth === 0) {
      hierarchy.color = 'black';
    } else if (hierarchy.depth === 1) {
      hierarchy.color = getColour(hierarchy.data.key);
    } else {
      hierarchy.color = hierarchy.parent.color;
    }
    if (hierarchy.children) {
      hierarchy.children.forEach((child) => colorHierarchy(child));
    }
  }

  const ref = useD3(
    (svg) => {
      if (data.length > 0) {
        svg.html('');

        const freedomNest = d3
          .nest()
          .key((d) => d.STATUS)
          .entries(data);

        const dataNested = {
          key: 'freedomNest',
          values: freedomNest,
        };

        const populationHierarchy = d3
          .hierarchy(dataNested, (d) => d.values)
          .sum((d) => d[mode]);

        console.log('data_nested ', dataNested);
        console.log('population_hierarchy ', populationHierarchy);

        svg
          .append('rect')
          .attr('width', '100%')
          .attr('height', '100%')
          .style('fill', '#fff');

        const voronoi = svg
          .append('g')
          .attr('transform', `translate('${margin.left}','${margin.top}')`);
        const labels = svg
          .append('g')
          .attr('transform', `translate('${margin.left}','${margin.top}')`);
        const popLabels = svg
          .append('g')
          .attr('transform', `translate('${margin.left}','${margin.top}')`);

        const seed = new Math.seedrandom(16);
        const voronoiTreeMap = d3
          .voronoiTreemap()
          .prng(seed)
          .clip(ellipse)
          .convergenceRatio(0.0000001)
          .minWeightRatio(0.000001)
          .maxIterationCount(56);

        voronoiTreeMap(populationHierarchy);
        colorHierarchy(populationHierarchy);

        const allNodes = populationHierarchy
          .descendants()
          .sort((a, b) => b.depth - a.depth)
          // eslint-disable-next-line prefer-object-spread
          .map((d, i) => Object.assign({}, d, { id: i }));

        const hoveredShape = null;

        voronoi
          .selectAll('path')
          .data(allNodes)
          .enter()
          .append('path')
          .attr('d', (d) => `M${d.polygon.join('L')}Z`)
          .attr('class', (d) => `polygons poly-${d.id}`)
          .style('fill', (d) => (d.parent ? d.parent.color : d.color))
          .attr('stroke', '#F5F5F2')
          .attr('stroke-width', (d) => (d.depth === 2 ? 1 : 0))
          .style('fill-opacity', (d) => (d.depth === 2 ? 0.9 : 0))
          .attr('pointer-events', (d) => (d.depth === 2 ? 'all' : 'none'))
          .on('mouseenter', (d) => {
            const label = labels.select(`.label-${d.id}`);
            label.attr('opacity', 1);
            const popLabel = popLabels.select(`.label-${d.id}`);
            popLabel.attr('opacity', 1);
            const poly = svg.select(`.poly-${d.id}`);
            console.log('poly ', poly);
            poly.attr('opacity', 0.6);
          })
          .on('mouseleave', (d) => {
            const label = labels.select(`.label-${d.id}`);
            label.attr('opacity', () => (d.data[mode] > level ? 1 : 0));
            const popLabel = popLabels.select(`.label-${d.id}`);
            popLabel.attr('opacity', () => {
              return d.data[mode] > level ? 1 : 0;
            });
            const polyAll = svg.selectAll(`.polygons`);
            polyAll.attr('opacity', 1);
          })
          .attr('stroke-width', (d) => 7 - d.depth * 2.8)
          .style('fill', (d) => d.color);

        labels
          .selectAll('text')
          .data(allNodes.filter((d) => d.depth === 2))
          .enter()
          .append('text')
          .attr('class', (d) => `label-${d.id}`)
          .attr('text-anchor', (d) => {
            if (d.polygon.site.x < 60) {
              return 'start';
            }
            if (d.polygon.site.x > stageWidth - 60) {
              return 'end';
            }
            return 'middle';
          })
          .attr('transform', (d) => {
            const yPos =
              d.polygon.site.y > stageWidth - 40
                ? d.polygon.site.y - 26
                : d.polygon.site.y + 6;
            return `translate(${[d.polygon.site.x, yPos]})`;
          })
          .text((d) => d.data.key || d.data.NAME)
          // .attr('opacity', d => d.data.key === hoveredShape ? 1 : 0)
          .attr('opacity', (d) => {
            if (d.data.key === hoveredShape) {
              return 1;
            }
            if (d.data[mode] > level) {
              return 1;
            }
            return 0;
          })
          .attr('cursor', 'default')
          .attr('pointer-events', 'none')
          .attr('fill', 'black')
          .style('font-family', 'Raleway')
          .style('text-shadow', '2px 2px 3px white');

        popLabels
          .selectAll('text')
          .data(allNodes.filter((d) => d.depth === 2))
          .enter()
          .append('text')
          .attr('class', (d) => `label-${d.id}`)
          .attr('text-anchor', (d) => {
            if (d.polygon.site.x < 60) {
              return 'start';
            }
            if (d.polygon.site.x > stageWidth - 60) {
              return 'end';
            }
            return 'middle';
          })
          .attr('transform', (d) => {
            const yPos =
              d.polygon.site.y > stageWidth - 40
                ? d.polygon.site.y - 7
                : d.polygon.site.y + 25;
            return `translate(${[d.polygon.site.x, yPos]})`;
          })
          .text((d) => d.data.YEAR)
          // .attr('opacity', d => d.data.key === hoveredShape ? 1 : 0)
          .attr('opacity', (d) => {
            if (d.data.key === hoveredShape) {
              return 1;
            }
            if (d.data[mode] > level) {
              return 1;
            }
            return 0;
          })
          .attr('cursor', 'default')
          .attr('pointer-events', 'none')
          .attr('fill', 'black')
          .style('font-size', '12px')
          .style('font-family', 'Raleway')
          .style('text-shadow', '2px 2px 3px white');
      }
    },
    [data],
  );

  const style = {
    width: stageWidth,
    height: stageWidth,
    marginRight: '0px',
    marginLeft: '0px',
  };

  return (
    <section className="content-wrap">
      <svg ref={ref} style={style} />
    </section>
  );
}
